import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Navigate } from 'react-router-dom';
import { RequirementSlotsSkeleton } from '../components/SkeletonLoader';
import { useWebSocketEvent } from '../hooks/useWebSocket';
import {
  getAccreditationChecklist,
  getAccreditationDocuments,
  getBulkAccreditationRemarks,
  uploadAccreditationDocument,
  uploadBulkAccreditationDocuments,
  deleteAccreditationDocument,
  getCurrentAcademicYear,
  getMyOrganization,
  getAccreditationCertificate,
  submitWaiverLink,
  getWaiverLink,
  deleteWaiverLink,
  BASE_URL,
} from '../services/api';

const AiStatusBadge = ({ status }) => {
  if (status === 'passed') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-purple-100 text-purple-600">
        Passed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-yellow-100 text-yellow-700">
      Noted
    </span>
  );
};

const CoordinatorStatusBadge = ({ status }) => {
  if (status === 'reviewed') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-green-100 text-[#10B981]">
        Reviewed
      </span>
    );
  }
  if (status === 'revision') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-orange-100 text-orange-600">
        For Revision/s
      </span>
    );
  }
  if (status === 'rejected') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-red-100 text-red-600">
        Rejected
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-yellow-100 text-yellow-700">
      Pending
    </span>
  );
};

const getCompiledSlotHint = (name) => {
  const n = (name || '').toLowerCase();
  if (n.includes('three') && n.includes('seminar')) {
    return 'Please compile the Seminar Summary Table and all 3 Seminar Attendance Certificates into 1 single PDF file before uploading.';
  }
  return null;
};

// Returns the per-slot max upload size in MB (must mirror backend _SLOT_LIMITS_MB)
const getSlotMaxMB = (name) => {
  const n = (name || '').toLowerCase();
  if (n.includes('narrative report')) return 50;
  if (n.includes('financial statement')) return 20;
  if (n.includes('constitution')) return 20;
  if (n.includes('1x1 picture') || n.includes('contact details')) return 15;
  if (n.includes('waiver')) return 50;
  return 10; // default
};


const DocumentCard = ({
  doc,
  onUpload,
  onView,
  onCancel,
  deletingId,
  waiverLink,
  waiverLinkInput,
  onWaiverLinkInputChange,
  waiverLinkLoading,
  waiverLinkError,
  onSubmitWaiverLink,
  onDeleteWaiverLink,
  showWaiverModal,
  setShowWaiverModal,
}) => {
  const attachments = doc.documents || [];
  const isUploaded = attachments.length > 0;
  const compileHint = getCompiledSlotHint(doc.name);
  const isWaiver = (doc.name || '').toLowerCase().includes('waiver');

  // State when document is NOT YET UPLOADED
  if (!isUploaded) {
    return (
      <div className="bg-white rounded-[16px] border border-gray-200 shadow-sm overflow-hidden">
        {/* Card Header */}
        <div className="px-4 sm:px-5 py-3.5 bg-[#086C53] text-white">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center space-x-3 min-w-0 flex-1">
              <div className="w-7 h-7 rounded-lg bg-white/15 text-white border border-white/20 flex items-center justify-center font-black text-[12px] flex-shrink-0">
                {doc.id}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center space-x-2">
                  <p className="text-[13px] sm:text-[14px] font-bold text-white leading-snug break-words">{doc.name}</p>
                </div>
                <p className="text-[11px] text-emerald-200/90 font-medium">Required</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 flex-shrink-0">
              {doc.name.toLowerCase().includes('waiver') ? (
                <button
                  onClick={() => setShowWaiverModal(true)}
                  className="px-4 py-1.5 bg-white text-[#1B5446] rounded-lg text-[11px] sm:text-[12px] font-bold hover:bg-emerald-50 transition-colors flex-shrink-0 shadow-xs cursor-pointer min-w-[72px] text-center"
                >
                  Link
                </button>
              ) : (
                <button
                  onClick={onUpload}
                  className="px-4 py-1.5 bg-white text-[#1B5446] rounded-lg text-[11px] sm:text-[12px] font-bold hover:bg-emerald-50 transition-colors flex-shrink-0 shadow-xs cursor-pointer"
                >
                  Upload
                </button>
              )}
            </div>
          </div>
          {compileHint && (
            <div className="mt-2.5">
              <p className="text-[11px] text-emerald-100 bg-white/10 border border-white/20 rounded-md px-2.5 py-1 font-medium inline-block max-w-full leading-relaxed">
                {compileHint}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // State when document IS UPLOADED (Single or Multiple)
  return (
    <div className="bg-white rounded-[16px] border border-gray-200 shadow-sm overflow-hidden">
      {/* Card Header */}
      <div className="px-4 sm:px-5 py-3.5 bg-[#086C53] text-white">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center space-x-3 min-w-0 flex-1">
            <div className="w-7 h-7 rounded-lg bg-white/15 text-white border border-white/20 flex items-center justify-center font-black text-[12px] flex-shrink-0">
              {doc.id}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center space-x-2">
                <p className="text-[13px] sm:text-[14px] font-bold text-white leading-snug break-words">{doc.name}</p>
              </div>
              <p className="text-[11px] text-emerald-200/90 font-medium">Required</p>
            </div>
          </div>
          <div className="flex items-center space-x-2 flex-shrink-0">
            {!(doc.name.toLowerCase().includes('waiver') && waiverLink) && (
              <button
                onClick={onUpload}
                className="px-3 py-1.5 bg-white text-[#1B5446] rounded-lg text-[11px] sm:text-[12px] font-bold hover:bg-emerald-50 transition-colors cursor-pointer flex items-center space-x-1 whitespace-nowrap shadow-xs"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                </svg>
                <span>Add File</span>
              </button>
            )}
          </div>
        </div>
        {compileHint && !(doc.name.toLowerCase().includes('waiver') && waiverLink) && (
          <div className="mt-2.5">
            <p className="text-[11px] text-emerald-100 bg-white/10 border border-white/20 rounded-md px-2.5 py-1 font-medium inline-block max-w-full leading-relaxed">
              {compileHint}
            </p>
          </div>
        )}
      </div>

      {/* Aggregate Requirement Status Banner */}
      <div className="mx-4 mt-3 flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-2">
        {!isWaiver && (
          <div className="flex items-center justify-between bg-[#FBFBFB] px-4 py-2 rounded-[8px] flex-1 w-full">
            <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider">Overall AI</span>
            <AiStatusBadge status={doc.aiStatus} />
          </div>
        )}
        <div className="flex items-center justify-between bg-[#FBFBFB] px-4 py-2 rounded-[8px] flex-1 w-full">
          <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider">Overall Status</span>
          <CoordinatorStatusBadge status={doc.coordinatorStatus} />
        </div>
      </div>

      {/* Individual Attachments List */}
      <div className="p-4 space-y-3">
        {attachments.map((att, attIdx) => {
          const canCancelAtt = !att.adminReviewed;

          return (
            <div
              key={att.accreditation_document_id || attIdx}
              className="bg-gray-50/70 rounded-xl border border-gray-200/80 p-3.5 shadow-xs hover:border-gray-300 transition-all space-y-2.5"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center space-x-2.5 min-w-0 flex-1">
                  <div className="w-7 h-7 rounded-md bg-gray-200/70 flex items-center justify-center text-gray-500 font-bold text-[10px] flex-shrink-0">
                    {att.type}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[12px] font-semibold text-gray-700 truncate" title={att.fileName}>
                      {att.fileName}
                    </p>
                    <p className="text-[10px] text-gray-400">
                      {att.submitted_at ? new Date(att.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Uploaded'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-1.5 flex-shrink-0">
                  <button
                    onClick={() => onView(att)}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-[11px] font-bold text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    View
                  </button>
                  {canCancelAtt && (
                    <button
                      onClick={() => onCancel(att)}
                      disabled={deletingId === (att.accreditation_document_id || att.reaccreditation_document_id)}
                      className="px-3 py-1 border border-red-200 rounded-lg text-[11px] font-bold text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors flex items-center space-x-1 cursor-pointer whitespace-nowrap"
                    >
                      {deletingId === (att.accreditation_document_id || att.reaccreditation_document_id) ? (
                        <span>Deleting...</span>
                      ) : (
                        <>
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          <span>Delete</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* Sub-Attachment Status Badges */}
              <div className="flex flex-wrap items-center gap-2 pt-1">
                {!isWaiver && (
                  <div className="flex items-center space-x-1.5 text-[11px]">
                    <span className="text-[10px] font-bold text-gray-400 uppercase">AI:</span>
                    <AiStatusBadge status={att.aiStatus} />
                  </div>
                )}
                <div className="flex items-center space-x-1.5 text-[11px]">
                  <span className="text-[10px] font-bold text-gray-400 uppercase">Coordinator:</span>
                  <CoordinatorStatusBadge status={att.adminReviewed ? att.coordinatorStatus : 'pending'} />
                </div>
              </div>

              {/* AI Issues */}
              {!isWaiver && att.aiNote && (
                <div className="rounded-[8px] bg-[#f3f0ff] border border-[#e0d9ff] p-2.5 text-[11px] text-gray-600 space-y-1">
                  <div className="flex items-center justify-between text-[#7c3aed] font-bold text-[10px] uppercase">
                    <span>AI Feedback</span>
                  </div>
                  <div>
                    {(() => {
                      const note = att.aiNote || '';
                      if (!note || note.trim().toLowerCase() === 'none' || note.trim().toLowerCase() === 'n/a') {
                        return (
                          <div className="text-emerald-700 font-medium flex items-center gap-1.5 py-0.5">
                            <span className="text-xs">✓</span> No issues detected. Document looks complete and compliant!
                          </div>
                        );
                      }

                      // Robust parsing: strip redundant numbering, prevent mid-sentence breaks, and rejoin wrapped lines
                      const rawLines = note.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
                      let items = [];

                      if (rawLines.length > 1) {
                        for (const line of rawLines) {
                          // Prevent splitting if previous item has unclosed parenthesis (e.g. "... (Page")
                          const hasUnclosedParen = items.length > 0 &&
                            ((items[items.length - 1].match(/\(/g) || []).length >
                              (items[items.length - 1].match(/\)/g) || []).length);

                          const match = !hasUnclosedParen && line.match(/^(\d+[\.\:]|[•\-\*])\s*(.*)$/);
                          if (match) {
                            items.push(match[2].trim());
                          } else if (items.length > 0) {
                            // Rejoin continuation of previous sentence that wrapped unexpectedly
                            items[items.length - 1] += ' ' + line;
                          } else {
                            items.push(line);
                          }
                        }
                      } else {
                        // Single line: check for multiple inline numbered points like "1. ... 2. ..."
                        // NEVER split on parentheticals like "(Page 1)" or inline numbers without a dot
                        const singleText = rawLines[0] || note.trim();
                        const splitParts = singleText
                          .split(/(?<=[.;!?]|^)\s*(?=\d+\.\s+[A-Z])/i)
                          .map(s => s.trim())
                          .filter(Boolean);

                        if (splitParts.length > 1) {
                          items = splitParts;
                        } else {
                          items = [singleText];
                        }
                      }

                      // Ensure all leading markers (e.g. "1. ", "2. ", "• ") are stripped cleanly
                      const cleanItems = items
                        .map(s => s.replace(/^(\d+[\.\:]|[•\-\*])\s*/, '').trim())
                        .filter(s => {
                          const c = s.toLowerCase().replace(/[^a-z]/g, '');
                          return s.length > 0 && c !== 'none' && c !== 'na' && c !== 'null';
                        });

                      if (cleanItems.length === 0) {
                        return <div className="text-gray-700 leading-relaxed">{note}</div>;
                      }

                      return (
                        <ul className="space-y-1.5 list-none pl-0 mt-1">
                          {cleanItems.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-2 bg-white/70 border border-purple-100 rounded-lg p-2 shadow-2xs">
                              <span className="inline-flex items-center justify-center w-4 h-4 rounded-sm bg-gray-200 text-purple-800 text-[9px] font-black shrink-0 mt-0.5">
                                {idx + 1}
                              </span>
                              <span className="leading-snug text-gray-700 font-medium">{item}</span>
                            </li>
                          ))}
                        </ul>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Coordinator Note */}
              {att.adminReviewed && att.coordinatorNote && (
                <div className="rounded-[8px] bg-green-50 border border-green-200 p-2.5 text-[11px] text-gray-700 space-y-1">
                  <div className="text-green-700 font-bold text-[10px] uppercase">Coordinator Remarks</div>
                  <div>{att.coordinatorNote}</div>
                </div>
              )}

              {doc.name.toLowerCase().includes('waiver') && waiverLink && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Waiver Link</p>
                  <a
                    href={waiverLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[13px] text-blue-600 underline break-all hover:text-blue-800 transition-colors"
                  >
                    {waiverLink}
                  </a>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const OrgAccreditation = () => {
  const [organizationId, setOrganizationId] = useState(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      return user ? user.organization_id : null;
    } catch (e) {
      return null;
    }
  });
  const [checklist, setChecklist] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processType, setProcessType] = useState('accreditation');

  const [viewingDoc, setViewingDoc] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [newReqFiles, setNewReqFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [deadlinePassed, setDeadlinePassed] = useState(false);
  const [deadlineSoon, setDeadlineSoon] = useState(false);
  const [deadlineDate, setDeadlineDate] = useState(null);
  const [orgData, setOrgData] = useState(null);
  const [accredCert, setAccredCert] = useState(null);
  const [viewingCert, setViewingCert] = useState(null);
  const [waiverLink, setWaiverLink] = useState('');
  const [waiverLinkInput, setWaiverLinkInput] = useState('');
  const [waiverLinkLoading, setWaiverLinkLoading] = useState(false);
  const [waiverLinkError, setWaiverLinkError] = useState('');
  const [showWaiverModal, setShowWaiverModal] = useState(false);

  const uploadAbortControllerRef = useRef(null);

  const [validationElapsed, setValidationElapsed] = useState(0);
  const [estimatedDuration, setEstimatedDuration] = useState(60);
  const [hasStartedValidation, setHasStartedValidation] = useState(false);
  const [validationFinishing, setValidationFinishing] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Estimate total validation seconds from total file size
  const getEstimatedDuration = (files) => {
    const totalMB = (files || []).reduce((sum, f) => sum + f.size / (1024 * 1024), 0);
    if (totalMB < 0.3) return 20;
    if (totalMB < 0.8) return 30;
    if (totalMB < 2) return 45;
    if (totalMB < 5) return 70;
    if (totalMB < 10) return 100;
    return 140;
  };

  // Returns a message that genuinely reflects where we are in the validation timeline
  const getValidationMessage = (elapsed, estimated) => {
    const p = Math.min(elapsed / estimated, 0.99);
    if (p < 0.08) return 'Uploading document...';
    if (p < 0.20) return 'Processing document structure...';
    if (p < 0.34) return 'Extracting and reading text content...';
    if (p < 0.48) return 'Analyzing document content...';
    if (p < 0.61) return 'Checking accreditation guidelines...';
    if (p < 0.73) return 'Cross-referencing compliance criteria...';
    if (p < 0.83) return 'Evaluating completeness and accuracy...';
    if (p < 0.92) return 'Generating validation report...';
    return 'Almost done, finalizing results...';
  };

  useEffect(() => {
    if (!submitting) {
      setValidationElapsed(0);
      return;
    }
    setValidationElapsed(0);
    const timer = setInterval(() => {
      setValidationElapsed((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [submitting]);

  const handleCloseUploadModal = () => {
    if (uploadAbortControllerRef.current) {
      try {
        uploadAbortControllerRef.current.abort();
      } catch (err) {
        console.error('Error aborting upload:', err);
      }
      uploadAbortControllerRef.current = null;
    }
    setUploadingDoc(null);
    setNewReqFiles([]);
    setIsDragOver(false);
    setSubmitError('');
    setSubmitting(false);
    setHasStartedValidation(false);
    setValidationFinishing(false);
  };

  // Aborts the ongoing validation but keeps the modal open so the user can re-upload
  const handleCancelValidation = () => {
    if (uploadAbortControllerRef.current) {
      try {
        uploadAbortControllerRef.current.abort();
      } catch (err) {
        console.error('Error aborting upload:', err);
      }
      uploadAbortControllerRef.current = null;
    }
    setNewReqFiles([]);
    setSubmitting(false);
    setHasStartedValidation(false);
    setSubmitError('');
    setValidationFinishing(false);
  };

  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      if (user && user.organization_id) setOrganizationId(user.organization_id);
    } catch (e) {
      // ignore
    }
  }, []);

  const mapAiStatus = (recommendation) => {
    if (!recommendation) return null;
    return recommendation === 'Approved' ? 'passed' : 'flagged';
  };

  const mapCoordinatorStatus = (status) => {
    const map = {
      reviewed: 'reviewed',
      for_revision: 'revision',
      rejected: 'rejected',
      pending: 'pending',
    };
    return map[status] || 'pending';
  };

  const getFileType = (filePath) => {
    const path = (filePath || '').toLowerCase();
    if (path.endsWith('.pdf')) return 'PDF';
    if (path.endsWith('.doc') || path.endsWith('.docx')) return 'DOC';
    if (path.endsWith('.png') || path.endsWith('.jpg') || path.endsWith('.jpeg')) return 'IMG';
    return 'FILE';
  };

  const fetchData = async (isBackground = false) => {
    const targetOrgId = organizationId || (() => {
      try {
        return JSON.parse(localStorage.getItem('user'))?.organization_id;
      } catch {
        return null;
      }
    })();

    if (!targetOrgId) {
      if (!isBackground) setLoading(false);
      return;
    }

    try {
      if (!isBackground) setLoading(true);
      const [checklistRes, yearRes, orgRes] = await Promise.all([
        getAccreditationChecklist(targetOrgId),
        getCurrentAcademicYear().catch(() => ({ data: null })),
        getMyOrganization().catch(() => ({ data: null })),
      ]);

      if (orgRes.data) setOrgData(orgRes.data);

      const pType = checklistRes.data.process_type || 'accreditation';
      setProcessType(pType);
      setChecklist(checklistRes.data);

      if (yearRes.data && yearRes.data.lock_date) {
        const lockDate = new Date(yearRes.data.lock_date);
        setDeadlineDate(lockDate);
        const now = new Date();
        const daysLeft = (lockDate - now) / (1000 * 60 * 60 * 24);
        if (now > lockDate) setDeadlinePassed(true);
        else if (daysLeft <= 7) setDeadlineSoon(true);
      }

      const [docsRes, bulkRemarksRes] = await Promise.all([
        getAccreditationDocuments(pType, targetOrgId),
        getBulkAccreditationRemarks(pType, targetOrgId).catch(() => ({ data: {} })),
      ]);
      const allDocs = docsRes.data || [];
      const remarksByDocId = bulkRemarksRes.data || {};

      const merged = checklistRes.data.items.map((item, idx) => {
        const slotDocs = allDocs.filter(
          (d) => (d.document_name || '').trim().toLowerCase() === (item.document_name || '').trim().toLowerCase()
        );

        if (slotDocs.length === 0) {
          return {
            id: idx + 1,
            name: item.document_name,
            type: null,
            aiStatus: null,
            coordinatorStatus: null,
            aiNote: null,
            coordinatorNote: null,
            adminReviewed: false,
            accreditation_document_id: null,
            documents: [],
          };
        }

        const sortedSlotDocs = [...slotDocs].sort((a, b) => {
          const dateA = a.submitted_at ? new Date(a.submitted_at).getTime() : 0;
          const dateB = b.submitted_at ? new Date(b.submitted_at).getTime() : 0;
          return dateA - dateB; // ascending: first submitted = top, latest = bottom
        });

        const mappedAttachments = sortedSlotDocs.map((doc, docIdx) => {
          const docId = doc.accreditation_document_id || doc.reaccreditation_document_id;
          const docRemarks = remarksByDocId[docId] || [];
          const latestWithContent = [...docRemarks].reverse().find((r) => r.content && r.content.trim());
          const coordinatorNote = latestWithContent ? latestWithContent.content : null;

          return {
            subId: docIdx + 1,
            name: item.document_name,
            accreditation_document_id: docId,
            file_path: doc.file_path,
            type: getFileType(doc.file_path),
            fileName: doc.file_path ? doc.file_path.split('/').pop().split('\\').pop() : `Attachment ${docIdx + 1}`,
            aiStatus: mapAiStatus(doc.validation_recommendation),
            coordinatorStatus: mapCoordinatorStatus(doc.status),
            aiNote: doc.validation_issues || null,
            coordinatorNote: coordinatorNote,
            adminReviewed: Boolean(doc.admin_reviewed),
            validation_score: doc.validation_score,
            tokens_used: doc.tokens_used,
            submitted_at: doc.submitted_at,
          };
        });

        const latestDoc = sortedSlotDocs[sortedSlotDocs.length - 1];
        const latestDocId = latestDoc.accreditation_document_id || latestDoc.reaccreditation_document_id;
        const slotRemarks = remarksByDocId[latestDocId] || [];
        const latestSlotWithContent = [...slotRemarks].reverse().find((r) => r.content && r.content.trim());

        let aggregateAiStatus = null;
        let aggregateCoordStatus = null;
        let aggregateAdminReviewed = false;

        // Unified Single-Entity aggregation: If ANY submitted subdocument has AI Passed → Overall AI Passed
        if (slotDocs.some((d) => d.validation_recommendation === 'Approved')) {
          aggregateAiStatus = 'passed';
        } else if (slotDocs.some((d) => d.validation_recommendation === 'Needs Revision' || d.validation_recommendation === 'Rejected')) {
          aggregateAiStatus = 'flagged';
        } else {
          aggregateAiStatus = mapAiStatus(latestDoc.validation_recommendation);
        }
        aggregateCoordStatus = mapCoordinatorStatus(latestDoc.status || item.status);
        aggregateAdminReviewed = Boolean(latestDoc.admin_reviewed);

        return {
          id: idx + 1,
          name: item.document_name,
          type: getFileType(latestDoc.file_path),
          aiStatus: aggregateAiStatus,
          coordinatorStatus: aggregateCoordStatus,
          aiNote: latestDoc.validation_issues || null,
          coordinatorNote: latestSlotWithContent ? latestSlotWithContent.content : null,
          adminReviewed: aggregateAdminReviewed,
          accreditation_document_id: latestDocId,
          file_path: latestDoc.file_path,
          documents: mappedAttachments,
        };
      });

      setDocuments(merged);
      fetchWaiverLink(targetOrgId, pType);
    } catch (error) {
      console.error('Failed to fetch accreditation data:', error);
    } finally {
      if (!isBackground) setLoading(false);
    }
  };

  const fetchAccredCertificate = async () => {
    try {
      const res = await getAccreditationCertificate(organizationId);
      setAccredCert(res.data || null);
    } catch (error) {
      setAccredCert(null);
    }
  };

  const fetchWaiverLink = async (orgId, pType) => {
    try {
      const res = await getWaiverLink(orgId, pType);
      setWaiverLink(res.data.waiver_link || '');
    } catch (error) {
      setWaiverLink('');
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchData();
    fetchAccredCertificate();
  }, [organizationId]);

  // Real-time synchronization
  useWebSocketEvent("DOCUMENT_UPDATED", (data) => {
    const currentOrgId = organizationId || (() => {
      try { return JSON.parse(localStorage.getItem('user'))?.organization_id; } catch { return null; }
    })();
    if (!data?.org_id || String(data.org_id) === String(currentOrgId)) {
      fetchData(true);
    }
  });

  useWebSocketEvent("ORGANIZATION_STATUS_UPDATED", (data) => {
    const currentOrgId = organizationId || (() => {
      try { return JSON.parse(localStorage.getItem('user'))?.organization_id; } catch { return null; }
    })();
    if (!data?.organization_id || String(data.organization_id) === String(currentOrgId)) {
      fetchData(true);
      fetchAccredCertificate();
    }
  });

  // Submits the files to the backend — runs OCR and AI validation concurrently
  const handleSubmitFile = async () => {
    if (!newReqFiles || newReqFiles.length === 0 || !uploadingDoc) return;
    const controller = new AbortController();
    uploadAbortControllerRef.current = controller;
    setEstimatedDuration(getEstimatedDuration(newReqFiles));
    setSubmitting(true);
    setHasStartedValidation(true);
    setSubmitError('');
    try {
      if (newReqFiles.length === 1) {
        const formData = new FormData();
        formData.append('file', newReqFiles[0]);
        formData.append('organization_id', organizationId);
        formData.append('document_name', uploadingDoc.name);
        await uploadAccreditationDocument(processType, formData, { signal: controller.signal });
      } else {
        const formData = new FormData();
        newReqFiles.forEach((file) => formData.append('files', file));
        formData.append('organization_id', organizationId);
        const mappings = newReqFiles.map((_, idx) => ({
          index: idx,
          document_name: uploadingDoc.name,
        }));
        formData.append('mappings', JSON.stringify(mappings));
        formData.append('document_name', uploadingDoc.name);
        await uploadBulkAccreditationDocuments(processType, formData, { signal: controller.signal });
      }

      // Signal "almost done" — the upload succeeded, now refreshing data
      setValidationFinishing(true);
      await fetchData(true);
      setUploadingDoc(null);
      setNewReqFiles([]);
      setIsDragOver(false);
    } catch (error) {
      if (controller.signal.aborted || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        // User cancelled via X or Cancel Validation
        return;
      }
      if (error.userMessage) {
        setSubmitError(error.userMessage);
      } else if (error.response && error.response.data && error.response.data.detail) {
        setSubmitError(error.response.data.detail);
      } else {
        setSubmitError('Failed to upload the document. Please try again.');
      }
    } finally {
      setSubmitting(false);
      setValidationFinishing(false);
      uploadAbortControllerRef.current = null;
    }
  };

  // Cancels an unreviewed submission via the real backend.
  const handleCancel = async (doc) => {
    const docId = doc.accreditation_document_id || doc.reaccreditation_document_id;
    if (!docId) return;
    try {
      setDeletingId(docId);
      await deleteAccreditationDocument(processType, docId);
      await fetchData(true);
    } catch (error) {
      console.error('Failed to cancel submission:', error);
      const detail = error?.response?.data?.detail;
      let message = 'Failed to cancel this submission. Please try again.';
      if (detail === 'Cannot cancel a document that has already been reviewed by a coordinator') {
        message = 'This document has already been reviewed, so it can no longer be cancelled.';
      } else if (detail) {
        message = `Failed to cancel this submission: ${detail}`;
      }
      alert(message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleSubmitWaiverLink = async () => {
    if (!waiverLinkInput.trim()) return false;
    setWaiverLinkLoading(true);
    setWaiverLinkError('');
    try {
      await submitWaiverLink({
        link: waiverLinkInput.trim(),
        application_type: processType
      });
      setWaiverLink(waiverLinkInput.trim());
      setWaiverLinkInput('');
      fetchData(true);
      return true;
    } catch (error) {
      setWaiverLinkError(error.response?.data?.detail || 'Failed to submit waiver link.');
      return false;
    } finally {
      setWaiverLinkLoading(false);
    }
  };

  const handleDeleteWaiverLink = async () => {
    try {
      await deleteWaiverLink(organizationId, processType);
      setWaiverLink('');
      fetchData(true);
    } catch (error) {
      console.error('Failed to delete waiver link:', error);
    }
  };

  const reviewedCount = documents.filter((d) => d.adminReviewed && d.coordinatorStatus === 'reviewed').length;
  const pendingCount = documents.filter((d) => d.aiStatus !== null && !d.adminReviewed).length;
  const revisionCount = documents.filter((d) => d.adminReviewed && d.coordinatorStatus === 'revision').length;
  const rejectedCount = documents.filter((d) => d.adminReviewed && d.coordinatorStatus === 'rejected').length;
  const remainingCount = checklist ? checklist.not_submitted : 0;

  // Renders the real uploaded file inline (PDF/image), or a download link otherwise
  const renderFilePreview = () => {
    if (!viewingDoc) {
      return (
        <div className="text-center p-8 bg-white rounded-2xl border border-gray-200 shadow-sm max-w-lg">
          <p className="text-[14px] text-gray-500">No file has been submitted for this document yet.</p>
        </div>
      );
    }
    const docId = viewingDoc.accreditation_document_id || viewingDoc.reaccreditation_document_id;
    if (!docId) {
      return (
        <div className="text-center p-8 bg-white rounded-2xl border border-gray-200 shadow-sm max-w-lg">
          <p className="text-[14px] text-gray-500">No file has been submitted for this document yet.</p>
        </div>
      );
    }
    const fileUrl = `${BASE_URL}/documents/${processType}/${docId}/view`;
    const path = (viewingDoc.file_path || '').toLowerCase();
    const docType = (viewingDoc.type || '').toUpperCase();

    if (path.endsWith('.pdf') || docType === 'PDF') {
      return (
        <div className="w-full h-full bg-white rounded-xl shadow-inner overflow-hidden border border-gray-200">
          <iframe src={fileUrl} title={viewingDoc.name || viewingDoc.fileName || 'Document'} className="w-full h-full border-none" />
        </div>
      );
    }
    if (path.endsWith('.docx') || path.endsWith('.doc') || docType === 'DOC') {
      const previewUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true`;
      return (
        <div className="w-full h-full bg-white rounded-xl shadow-inner overflow-hidden border border-gray-200">
          <iframe src={previewUrl} title={viewingDoc.name || viewingDoc.fileName || 'Document'} className="w-full h-full border-none" />
        </div>
      );
    }
    if (path.endsWith('.png') || path.endsWith('.jpg') || path.endsWith('.jpeg') || docType === 'IMG') {
      return (
        <div className="max-w-full max-h-full flex items-center justify-center p-4">
          <img src={fileUrl} alt={viewingDoc.name || viewingDoc.fileName || 'Document'} className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-md" />
        </div>
      );
    }
    return (
      <div className="text-center p-8 bg-white rounded-2xl border border-gray-200 shadow-sm max-w-lg">
        <p className="text-[14px] text-gray-700 font-medium mb-3">Preview is not supported for this file format.</p>
        <a
          href={`${BASE_URL}/documents/${processType}/${docId}/download`}
          className="inline-flex items-center space-x-2 px-4 py-2 bg-[#086C53] text-white text-xs font-bold rounded-xl shadow-sm hover:bg-[#065440] transition-colors"
        >
          <span>Download Document</span>
        </a>
      </div>
    );
  };

  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem('user'));
    } catch {
      return null;
    }
  })();

  if (user?.application_type === 'offcampus') {
    return <Navigate to="/org/off-campus" replace />;
  }

  if (loading) {
    return <RequirementSlotsSkeleton count={8} titleWidth="w-64 sm:w-80" />;
  }

  return (
    <div className="space-y-4 w-full">
      {/* Page Header */}
      <div className="mb-4 space-y-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-[20px] sm:text-[24px] md:text-[28px] font-extrabold text-gray-900 leading-tight mb-1">
              {processType === 'reaccreditation' ? 'Re-accreditation Documents' : 'Accreditation Documents'}
            </h1>
          </div>
          {/* Organization accreditation status badge */}
          {orgData && (
            <div className={`px-3.5 py-1.5 rounded-xl text-[12px] sm:text-[13px] font-bold shadow-xs border self-start sm:self-auto flex-shrink-0 max-w-full text-center ${orgData.status === 'for_interview'
              ? 'bg-blue-50 border-blue-300 text-blue-700'
              : orgData.status === 'for_endorsement_accredited' || orgData.status === 'for_endorsement_reaccredited'
                ? 'bg-green-50 border-green-300 text-green-700'
                : 'bg-yellow-50 border-yellow-300 text-yellow-700'
              }`}>
              {orgData.status === 'for_endorsement_accredited' && 'For Endorsement to be Accredited'}
              {orgData.status === 'for_endorsement_reaccredited' && 'For Endorsement to be Re-accredited'}
              {orgData.status === 'for_interview' && (
                <span>
                  For Interview
                  {orgData.interview_date && `: ${new Date(orgData.interview_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                  {orgData.interview_medium && ` via ${orgData.interview_medium}`}
                </span>
              )}
              {orgData.status === 'pending' && (
                orgData.org_type === 'existing'
                  ? 'Re-accreditation Pending'
                  : 'Accreditation Pending'
              )}
            </div>
          )}
        </div>
      </div>

      {accredCert && (
        <div className="bg-[#054334] rounded-[14px] sm:rounded-[16px] p-3.5 sm:p-5 text-white shadow-md sm:shadow-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 sm:w-6 sm:h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] sm:text-[15px] font-black leading-snug">
                Congratulations! <span className="font-semibold text-emerald-100/90 text-[12px] sm:text-[14px] block sm:inline">Your organization has been officially accredited.</span>
              </p>
              {accredCert.uploaded_at && (
                <p className="text-[10px] sm:text-[11px] text-white/60 mt-0.5">
                  Issued: {new Date(accredCert.uploaded_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 self-end sm:self-auto flex-shrink-0 pt-0.5 sm:pt-0">
            <button
              onClick={() => setViewingCert({
                accreditation_certificate_id: accredCert.accreditation_certificate_id,
                file_path: accredCert.file_path,
                name: 'Accreditation Certificate',
              })}
              className="px-3.5 py-1.5 rounded-lg text-[11px] sm:text-[12px] font-bold bg-white/20 hover:bg-white/30 text-white transition-colors cursor-pointer"
            >
              View
            </button>
            <a
              href={`${BASE_URL}/documents/accreditation-cert/file/${accredCert.accreditation_certificate_id}/download`}
              className="px-3.5 py-1.5 rounded-lg text-white text-[11px] sm:text-[12px] font-bold bg-[#00A358] hover:bg-white hover:text-[#054334] transition-colors cursor-pointer"
            >
              Download
            </a>
          </div>
        </div>
      )}

      {deadlinePassed && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-4 flex items-start space-x-3">
          <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div>
            <p className="text-red-700 font-bold text-sm">Submission Deadline Has Passed</p>
            <p className="text-red-600 text-[12px] mt-0.5">
              The deadline was {deadlineDate ? deadlineDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}.
              Document uploads are no longer accepted.
            </p>
          </div>
        </div>
      )}

      {deadlineSoon && !deadlinePassed && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl px-5 py-4 flex items-start space-x-3">
          <svg className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="text-orange-700 font-bold text-sm">Deadline Approaching</p>
            <p className="text-orange-600 text-[12px] mt-0.5">
              The submission deadline is {deadlineDate ? deadlineDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}. Please submit your remaining documents as soon as possible.
            </p>
          </div>
        </div>
      )}

      {/* AI Summary Banner */}
      <div className="rounded-[12px] bg-[#f3f0ff] border border-[#e0d9ff] px-5 py-3.5 mb-4">
        <p className="text-[12px] text-gray-600 leading-relaxed">
          You have <strong>{reviewedCount}</strong> reviewed, <strong>{pendingCount}</strong> pending,{' '}
          <strong>{revisionCount}</strong> for revision, and <strong>{rejectedCount}</strong> rejected.
          {remainingCount > 0 && <> Please upload the remaining <strong>{remainingCount}</strong> document{remainingCount !== 1 ? 's' : ''}.</>}
        </p>
      </div>

      {/* Document Cards */}
      <div className="space-y-4">
        {documents.map((doc) => (
          <DocumentCard
            key={doc.name}
            doc={doc}
            onUpload={() => setUploadingDoc(doc)}
            onView={(att) => setViewingDoc(att)}
            onCancel={handleCancel}
            deletingId={deletingId}
            waiverLink={waiverLink}
            waiverLinkInput={waiverLinkInput}
            onWaiverLinkInputChange={setWaiverLinkInput}
            waiverLinkLoading={waiverLinkLoading}
            waiverLinkError={waiverLinkError}
            onSubmitWaiverLink={handleSubmitWaiverLink}
            onDeleteWaiverLink={handleDeleteWaiverLink}
            showWaiverModal={showWaiverModal}
            setShowWaiverModal={setShowWaiverModal}
          />
        ))}
      </div>

      {/* Upload Document Modal — Multi-file selection support */}
      {uploadingDoc && createPortal(
        <div className="fixed top-0 left-0 w-screen h-screen z-[9999] flex items-center justify-center p-4 sm:p-6">
          <div
            className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-40 backdrop-blur-sm"
            onClick={submitting ? undefined : handleCloseUploadModal}
          ></div>
          <div className="relative bg-white w-full max-w-[540px] rounded-[24px] shadow-2xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-8 pt-8 pb-4">
              <div>
                <h2 className="text-[20px] font-bold text-gray-900 leading-snug">Upload Document(s)</h2>
                <p className="text-[13px] text-[#086C53] font-bold mt-1 max-w-[380px] truncate">{uploadingDoc.name}</p>
              </div>
              <button
                type="button"
                onClick={submitting ? handleCancelValidation : handleCloseUploadModal}
                title={submitting ? "Cancel Validation" : "Close"}
                className="text-gray-400 hover:text-gray-600 transition-colors bg-gray-100 hover:bg-gray-200 rounded-full p-2 flex items-center justify-center cursor-pointer"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {submitError && (
              <div className="px-8 mb-2">
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-600 text-[12px] text-center font-medium whitespace-pre-line">{submitError}</div>
              </div>
            )}

            <div className="px-8 pb-2">
              {submitting ? (
                <div className="border border-gray-200/80 bg-gray-50/50 rounded-2xl p-10 flex flex-col items-center justify-center min-h-[220px] transition-all">
                  <div className="w-16 h-16 flex items-center justify-center mb-3">
                    <img
                      src="/assets/validation-spinner.png"
                      alt="Validating..."
                      className="w-14 h-14 object-contain animate-spin-slow"
                    />
                  </div>
                  <p
                    key={validationFinishing ? 'finishing' : getValidationMessage(validationElapsed, estimatedDuration)}
                    className="text-[13px] text-gray-500 font-medium text-center animate-fade-in"
                  >
                    {validationFinishing ? 'Almost done, finalizing results...' : getValidationMessage(validationElapsed, estimatedDuration)}
                  </p>
                </div>
              ) : (
                <div
                  className={`border-2 border-dashed rounded-2xl p-6 flex flex-col items-center justify-center cursor-pointer transition-all duration-200 ${isDragOver ? 'border-[#086C53] bg-[#086C53]/5' : newReqFiles.length > 0 ? 'border-[#086C53] bg-[#086C53]/5' : 'border-gray-300 hover:border-[#086C53] hover:bg-[#086C53]/5'
                    }`}
                  onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                  onDragLeave={() => setIsDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDragOver(false);
                    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                      const maxMB = getSlotMaxMB(uploadingDoc?.name);
                      const maxBytes = maxMB * 1024 * 1024;
                      const oversized = [];
                      const valid = Array.from(e.dataTransfer.files).filter(f => {
                        if (f.size > maxBytes) { oversized.push(f.name); return false; }
                        return true;
                      });
                      if (oversized.length > 0) {
                        setSubmitError(`The following file(s) exceed the ${maxMB}MB limit and were not uploaded: \n${oversized.join(', ')}.\n\nPlease reduce the file size and upload again.`);
                      }
                      if (valid.length > 0) {
                        setNewReqFiles((prev) => [...prev, ...valid]);
                        setHasStartedValidation(false);
                      }
                    }
                  }}
                  onClick={() => document.getElementById('user-req-file-input').click()}
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 transition-colors ${newReqFiles.length > 0 ? 'bg-[#086C53]/15' : 'bg-[#086C53]/10'}`}>
                    <svg className="w-6 h-6 text-[#086C53]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                  {newReqFiles.length === 0 ? (
                    <p className="text-[14px] text-gray-600 text-center">
                      Drag and drop multiple files, or{' '}
                      <span className="text-[#086C53] font-semibold underline underline-offset-2">Browse</span>
                    </p>
                  ) : (
                    <p className="text-[13px] text-gray-600 text-center font-semibold">
                      <span className="text-[#086C53] underline">Click to add more files</span> or drop them here
                    </p>
                  )}
                </div>
              )}

              <input
                id="user-req-file-input"
                type="file"
                multiple
                className="hidden"
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    const maxMB = getSlotMaxMB(uploadingDoc?.name);
                    const maxBytes = maxMB * 1024 * 1024;
                    const oversized = [];
                    const valid = Array.from(e.target.files).filter(f => {
                      if (f.size > maxBytes) { oversized.push(f.name); return false; }
                      return true;
                    });
                    if (oversized.length > 0) {
                      setSubmitError(`The following file(s) exceed the ${maxMB}MB limit and were not added: \n${oversized.join(', ')}.\n\nPlease reduce the file size and upload again.`);
                    }
                    if (valid.length > 0) {
                      setNewReqFiles((prev) => [...prev, ...valid]);
                      setHasStartedValidation(false);
                    }
                    e.target.value = '';
                  }
                }}
              />

              {/* Selected Files List */}
              {newReqFiles.length > 0 && (
                <div className="mt-3 space-y-1.5 max-h-[160px] overflow-y-auto custom-scrollbar pr-1">
                  <div className="flex items-center justify-between text-xs font-bold text-gray-600 mb-1 px-1">
                    <span>Selected Files ({newReqFiles.length}):</span>
                    {!submitting && !hasStartedValidation && (
                      <button
                        type="button"
                        onClick={() => setNewReqFiles([])}
                        className="text-red-500 hover:text-red-700 font-semibold cursor-pointer"
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                  {newReqFiles.map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-2 text-xs">
                      <div className="flex items-center space-x-2 truncate mr-2">
                        <span className="w-5 h-5 rounded-full bg-[#e0f5ee] text-[#086C53] flex items-center justify-center font-bold text-[10px] shrink-0">
                          {idx + 1}
                        </span>
                        <span className="font-medium text-gray-800 truncate" title={file.name}>{file.name}</span>
                        <span className="text-gray-400 shrink-0">({(file.size / (1024 * 1024)).toFixed(2)} MB)</span>
                      </div>
                      <button
                        type="button"
                        disabled={submitting}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!submitting) {
                            setNewReqFiles((prev) => prev.filter((_, i) => i !== idx));
                          }
                        }}
                        className={`text-gray-400 hover:text-red-500 p-1 rounded transition-colors shrink-0 ${submitting ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {!submitting && (
                <p className="text-[11px] text-gray-400 mt-3 text-center">
                  Max file size: <span className="font-semibold text-gray-500">{getSlotMaxMB(uploadingDoc?.name)} MB</span> per file · Selected file will be validated with AI. <br />
                </p>
              )}
            </div>

            {!submitting ? (
              <div className="px-8 pt-4 pb-8 flex flex-col space-y-2">
                <button
                  onClick={handleSubmitFile}
                  disabled={newReqFiles.length === 0}
                  className={`w-full py-3 rounded-xl text-[15px] font-bold transition-all duration-200 shadow-sm flex items-center justify-center space-x-2 ${newReqFiles.length > 0 ? 'bg-[#086C53] text-white hover:bg-[#065440] cursor-pointer' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                >
                  <span>
                    {newReqFiles.length > 1 ? `Submit ${newReqFiles.length} Files` : 'Submit File'}
                  </span>
                </button>
              </div>
            ) : (
              <div className="px-8 pt-4 pb-8">
                <button
                  type="button"
                  onClick={handleCancelValidation}
                  className="w-full py-3 rounded-xl text-[15px] font-bold bg-[#DD3D4A] text-white hover:bg-white transition-colors hover:text-black border border-grey cursor-pointer flex items-center justify-center space-x-2 shadow-sm"
                >
                  <span>Cancel</span>
                </button>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* View Document Modal — shows the real uploaded file */}
      {viewingDoc && createPortal(
        <div className="fixed top-0 left-0 w-screen h-screen z-[9999] flex items-center justify-center p-4 sm:p-6">
          <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-40 backdrop-blur-sm" onClick={() => setViewingDoc(null)}></div>
          <div className="relative bg-white w-full max-w-4xl h-[85vh] rounded-[24px] shadow-2xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h2 className="text-[18px] font-bold text-gray-900">{viewingDoc.name}</h2>
                <p className="text-[12px] text-gray-500 mt-0.5">Viewing {viewingDoc.type || 'document'}</p>
              </div>
              <button onClick={() => setViewingDoc(null)} className="p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 bg-gray-50 p-4 flex flex-col items-center justify-center overflow-hidden">
              {renderFilePreview()}
            </div>
          </div>
        </div>,
        document.body
      )}

      {viewingCert && createPortal(
        <div className="fixed top-0 left-0 w-screen h-screen z-[9999] flex items-center justify-center p-4 sm:p-6">
          <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-40 backdrop-blur-sm"
            onClick={() => setViewingCert(null)}></div>
          <div className="relative bg-white w-full max-w-4xl h-[85vh] rounded-[24px] shadow-2xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h2 className="text-[18px] font-bold text-gray-900">Accreditation Certificate</h2>
              </div>
              <button onClick={() => setViewingCert(null)}
                className="p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 bg-gray-50 p-4 overflow-hidden">
              <iframe
                src={`${BASE_URL}/documents/accreditation-cert/file/${viewingCert.accreditation_certificate_id}/view`}
                title="Accreditation Certificate"
                className="w-full h-full border-none rounded-xl"
              />
            </div>
          </div>
        </div>,
        document.body
      )}
      {showWaiverModal &&
        createPortal(
          <div
            className="fixed top-0 left-0 w-screen h-screen z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowWaiverModal(false)}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-[16px] font-bold text-gray-900">Submit Waiver Link</h3>
                <p className="text-[12px] text-gray-500 mt-1">
                  Paste your Google Drive link containing all members' signed waivers. <br />
                  <strong>Note:</strong> For student councils, the officer's signed waivers are enough.
                </p>
              </div>
              <div className="px-6 py-4 space-y-3">
                <input
                  type="url"
                  value={waiverLinkInput}
                  onChange={(e) => setWaiverLinkInput(e.target.value)}
                  placeholder="https://drive.google.com/file/..."
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-[13px] text-gray-800 focus:outline-none focus:border-[#086C53]"
                  autoFocus
                />
                {waiverLinkError && (
                  <p className="text-red-500 text-[12px]">{waiverLinkError}</p>
                )}
              </div>
              <div className="px-6 pb-5 flex gap-3">
                <button
                  onClick={() => {
                    setShowWaiverModal(false);
                    setWaiverLinkInput('');
                    setWaiverLinkError('');
                  }}
                  className="flex-1 py-2.5 rounded-xl text-[13px] font-bold border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    const success = await handleSubmitWaiverLink();
                    if (success) setShowWaiverModal(false);
                  }}
                  disabled={waiverLinkLoading || !waiverLinkInput.trim()}
                  className="flex-1 py-2.5 rounded-xl text-[13px] font-bold bg-[#086C53] text-white hover:bg-[#054334] transition-colors disabled:opacity-60"
                >
                  {waiverLinkLoading ? 'Submitting...' : 'Submit Link'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default OrgAccreditation;